import React, { useEffect, useState, useRef } from 'react';

function TypingText({
  text,
  speed = 20,
  onTypingFinished,
  onTypingUpdate
}: {
  text: string,
  speed?: number,
  onTypingFinished: (isTyping: boolean) => void,
  onTypingUpdate?: () => void
}) {
  const [displayedText, setDisplayedText] = useState('');
  const [index, setIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(true);

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const watchdogRef = useRef<NodeJS.Timeout | null>(null);

  // Restart typing when text changes
  useEffect(() => {
    setDisplayedText('');
    setIndex(0);
    setIsTyping(true);
  }, [text]);

  // Typing effect
  useEffect(() => {
    if (!isTyping || index >= text.length) return;

    timeoutRef.current = setTimeout(() => {
      setDisplayedText(prev => prev + text[index]);
      setIndex(prev => prev + 1);
      onTypingUpdate?.();
    }, speed);

    // Watchdog timeout: fallback if no typing in 2s
    if (watchdogRef.current) clearTimeout(watchdogRef.current);
    watchdogRef.current = setTimeout(() => {
      // console.warn('Typing stalled — forcing completion');
      finishTyping();
    }, 2000); // 2s watchdog

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [index, isTyping, text, speed, onTypingUpdate]);

  // When typing finishes
  useEffect(() => {
    if (index >= text.length && isTyping) {
      finishTyping();
    }
  }, [index, isTyping, text.length]);

  const finishTyping = () => {
    if (watchdogRef.current) clearTimeout(watchdogRef.current);
    setDisplayedText(text);
    setIsTyping(false);
    onTypingFinished(false);
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <span
        dangerouslySetInnerHTML={{
          __html: displayedText
            .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
            .replace(/(?<!\*)\*(?!\*)(.*?)\*(?!\*)/g, "<em>$1</em>")
            .replace(/###\s*(.*?)(?=\n|$)/g, "<h3>$1</h3>")
            .replace(/\n/g, "<br>")
            .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: inherit; text-decoration: underline;">$1</a>')
        }}
      />
      {isTyping && <div className="thinking-dot"></div>}
    </div>
  );
}

export default TypingText;